import { useState, useEffect, useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';
import useSocketIO from './useSocketIO';
import { toast } from 'react-toastify';

const useLocationStatus = entertainmentId => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();
  const { isConnected, on, off } = useSocketIO();
  const [briefingStatus, setBriefingStatus] = useState({
    status: 'offline',
    displayStatus: 'Player ist OFFLINE',
    receivedAt: null,
  });
  const [audioStatus, setAudioStatus] = useState({
    status: 'offline',
    displayStatus: 'Player ist OFFLINE',
    receivedAt: null,
  });
  const [lfStatus, setLfStatus] = useState({
    status: 'offline',
    displayStatus: 'Laserforce ist OFFLINE',
    receivedAt: null,
  });
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!entertainmentId) return;

    setLoading(true);
    try {
      const response = await sendRequest({
        url: `/api/v1/location-status/${entertainmentId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });

      if (response) {
        setBriefingStatus(
          response.briefing || {
            status: 'offline',
            displayStatus: 'Player ist OFFLINE',
            receivedAt: null,
          }
        );
        setAudioStatus(
          response.audio || {
            status: 'offline',
            displayStatus: 'Player ist OFFLINE',
            receivedAt: null,
          }
        );
        setLfStatus(
          response.lf || {
            status: 'offline',
            displayStatus: 'Laserforce ist OFFLINE',
            receivedAt: null,
          }
        );
      }
    } catch (err) {
      console.error('Error fetching location status:', err);
      // Set to offline on error
      setBriefingStatus({
        status: 'offline',
        displayStatus: 'Player ist OFFLINE',
        receivedAt: null,
      });
      setAudioStatus({ status: 'offline', displayStatus: 'Player ist OFFLINE', receivedAt: null });
      setLfStatus({
        status: 'offline',
        displayStatus: 'Laserforce ist OFFLINE',
        receivedAt: null,
      });
    } finally {
      setLoading(false);
    }
  }, [entertainmentId, authCtx]);

  // Listen for Socket.io status updates
  useEffect(() => {
    if (!entertainmentId || !isConnected) return;

    const handleStatusUpdate = data => {
      if (String(data.entertainmentId) === String(entertainmentId)) {
        if (data.category === 'briefing') {
          const statusMap = {
            looping: 'Ruhezustand',
            running: 'Video läuft',
            last30: 'Weniger als 30 Sekunden',
            waitingForEmployee: 'Warte auf Mitarbeiter',
            webview: 'Teameinteilung wird angezeigt',
            online: 'Player startet...',
            offline: 'Player ist OFFLINE',
          };
          setBriefingStatus({
            status: data.status,
            displayStatus: statusMap[data.status] || data.status,
            receivedAt: data.receivedAt,
          });
        } else if (data.category === 'audio') {
          const statusMap = {
            online: 'Player ist online',
            offline: 'Player ist OFFLINE',
            playing: 'Audio wird abgespielt',
          };
          setAudioStatus({
            status: data.status,
            displayStatus: statusMap[data.status] || data.status,
            receivedAt: data.receivedAt,
          });
        } else if (data.category === 'lf') {
          const statusMap = {
            online: 'Laserforce ist online',
            offline: 'Laserforce ist OFFLINE',
            'intro-start': 'Intro wird abgespielt...',
            start: 'Spiel gestartet',
            last3min: 'Letzte 3 Minuten',
            end: 'Spiel beendet',
          };
          setLfStatus({
            status: data.status,
            displayStatus: statusMap[data.status] || data.status,
            receivedAt: data.receivedAt,
          });
        }
      }
    };

    on('statusUpdate', handleStatusUpdate);

    return () => {
      off('statusUpdate', handleStatusUpdate);
    };
  }, [entertainmentId, isConnected, on, off]);

  // Initial fetch on mount and when entertainmentId changes
  useEffect(() => {
    fetchStatus();
  }, [entertainmentId]);

  const sendCommand = useCallback(
    async (command, payload, category) => {
      if (!entertainmentId) {
        toast.error('Keine Entertainment-ID verfügbar');
        return false;
      }

      try {
        await sendRequest({
          url: `/api/v1/location-command/${entertainmentId}`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            command,
            payload,
            category,
          },
        });
        return true;
      } catch (err) {
        toast.error('Fehler beim Senden des Befehls');
        console.error('Error sending command:', err);
        return false;
      }
    },
    [entertainmentId, authCtx]
  );

  return {
    briefingStatus,
    audioStatus,
    lfStatus,
    loading,
    refreshStatus: fetchStatus,
    sendCommand,
    isConnected,
  };
};

export default useLocationStatus;
